import React, { useMemo } from 'react';
import { useIsMobile } from '@hooks/responsive/useIsMobile';
import styled from '@emotion/styled';

// colors
import { primary, blue, gray, red, white, black } from '@styles/Colors';

// components
import FileInput from '@components/_atoms/FileInput';
import Typography from '@components/_atoms/Typography';
import CheckBox from '@components/_molecules/CheckBox';
import TextArea from '@components/_atoms/TextArea';

// constants
import { FileInputCheckBoxType, TypoVariant } from '@constants/atoms';

// 다국어 지원
import { useTranslation } from 'next-i18next';

// type
import { SystemPostPresignedUrlPayload } from 'models/swagger/data-contracts';

type Props = {
  title?: string;
  description?: string;
  fileInput?: FileInputType;
  checkBox?: CheckBoxType;
  textarea?: TextAreaType;
  isRequired?: boolean;
  isDescription?: boolean;
};

type FileInputType = {
  name?: string;
  cdnKey?: string;
  formFileName?: string;
  target: SystemPostPresignedUrlPayload['target'];
  handleFileUpload: (file: File, cdnUrl: string, name: string) => void;
  handleFileRemove: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  accept?: string;
};

type CheckBoxType = {
  type?: FileInputCheckBoxType;
  checkStatus?: boolean;
  onClick?: any;
};

type TextAreaType = {
  name?: string;
  value?: string;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
};

const FileInputBoxWrapper = styled.div`
  display: grid;
  row-gap: 8px;
`;

const Description = styled.div``;

const HighLightRed = styled.span`
  color: ${red.red3};
`;

export default function FileInputBox({
  title,
  description,
  fileInput,
  checkBox,
  textarea,
  isRequired = false,
  isDescription = true,
}: Props) {
  const isMobile = useIsMobile();

  // 다국어 지원
  const { i18n, t } = useTranslation('component');
  const ko = i18n.language === 'ko';

  return (
    <FileInputBoxWrapper>
      <>
        {title && (
          <Typography variant={isMobile ? TypoVariant.SH4 : TypoVariant.SH3} color={primary.gray}>
            {title}
            {isRequired && <HighLightRed> *</HighLightRed>}
          </Typography>
        )}
        {description && (
          <Typography variant={isMobile ? TypoVariant.B3 : TypoVariant.B4} color={primary.gray}>
            {description}
          </Typography>
        )}
      </>
      <>
        {fileInput && (
          <>
            {textarea && (
              <TextArea
                name={textarea?.name}
                value={textarea?.value}
                disabled={checkBox?.type === FileInputCheckBoxType.DISABLED}
                onChange={textarea?.onChange}
                placeholder={textarea.placeholder}
              />
            )}
            <FileInput
              name={fileInput?.name}
              target={fileInput?.target}
              cdnKey={fileInput?.cdnKey}
              formFileName={fileInput?.formFileName}
              handleFileUpload={fileInput?.handleFileUpload}
              handleFileRemove={fileInput?.handleFileRemove}
              disabled={fileInput?.disabled}
              accept={fileInput.accept}
            />
          </>
        )}
      </>
      {isDescription && (
        <>
          <Description>
            <Typography variant={TypoVariant.B4} color={gray.gray7}>
              {t('fileInput.description')}
            </Typography>
          </Description>
        </>
      )}
    </FileInputBoxWrapper>
  );
}
